import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import useProductParams from "../../../../hooks/useProductParams";
import LayoutAuthenticated from "@/src/containers/Layouts/LayoutAuthenticated";
import HintBox from "../../../Box/HintBox/HintBox";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import React, { useState } from "react";
import LegalbirdLoader from "../../../ContentLoader/LegalbirdLoader";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import { getCaseLink, getProductData, getProductNameBy } from "@/src/services/productService";
import FinalProcessFeedbackFlexServiceClient from "./Modules/FinalProcessFeedbackFlexServiceClient";
import Button from "@mui/material/Button";
import { Link, useNavigate } from "react-router";
import _cloneDeep from "lodash/cloneDeep";
import ApiClient from "../../../../services/apiClient";
import { STATUS_COMPLETED } from "../../../../services/contractLaw/leadStatus";
import { isFlexServiceProduct } from "../../../../services/advocardService";
import { useInvalidateInquiryToCustomer } from "@/src/hooks/queries/inquiryToCustomer";

const FlexServiceFeedback = () => {
  const { productName, productId } = useProductParams();
  const productClassName = getProductNameBy("urlPartShort", productName);
  const productApiUri = getProductData(productClassName, "apiUri");
  const { data: product, isLoading: isLoadingProduct } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId)
  });
  const [isLoading] = useState(false);
  const queryClient = useQueryClient();
  const { invalidateInquiryToCustomer } = useInvalidateInquiryToCustomer();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data);
      await invalidateInquiryToCustomer();
    },
  });
  const navigate = useNavigate();

  if (isLoadingProduct) {
    return (
      <LayoutAuthenticated>
        <LegalbirdLoader />
      </LayoutAuthenticated>
    );
  }

  if (!!product.finalClaimStatusClient || product.leadStatus !== STATUS_COMPLETED || !isFlexServiceProduct(product)) {
    return (
      <LayoutAuthenticated>
        <HintBox headline={"Keine Berechtigung"}>
          Sie haben aktuell nicht die Berechtigung diese Seite aufzurufen.
          <Button component={Link} to={getCaseLink(product)} variant={"text"} color={"primary"}>
            Zurück zum Fall
          </Button>
        </HintBox>
      </LayoutAuthenticated>
    );
  }

  const initialValues = {
    clientReaction: null,
    finalProcessFeedbackClient: null,
  };

  const handleSubmit = async ({ values }: { values: AbstractCase }) => {
    const updateData = _cloneDeep(values);
    if (updateData.clientReaction === "closeCase") {
      updateData.finalClaimStatusClient = "fullyAgreed";
      await updateMutation.mutateAsync({
        uri: getProductData(product.name, "apiUri"),
        id: product.id,
        data: updateData,
      });
    } else if (updateData.clientReaction === "continueNewCase") {
      await ApiClient.post(`/advocard/flex/reset_case/${product.productCategory}/${product.id}`);
    }

    navigate(getCaseLink(product) + "/flex/rueckmeldung/bestaetigung", { state: "sendForm" });
  };

  return (
    <LayoutAuthenticated>
      <HintBox headline={"Weiteres Vorgehen notwendig"}>
        <p>
          <strong>Wenn Sie der Meinung sind, dass Sie weiteren rechtlichen Beistand benötigen, dann können Sie uns das hier mitteilen</strong>
          <br />
          <br />
          Bitte geben Sie uns hier eine Rückmeldung darüber, wie Sie weiter vorgehen möchten.
        </p>
      </HintBox>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <FinalProcessFeedbackFlexServiceClient isLoading={isLoading} />
      </FormProvider>
    </LayoutAuthenticated>
  );
};

export default FlexServiceFeedback;
